/**
 * Team Inbox — SHARED plain-text linkifier.
 *
 * Turns URLs, Brevo-style `[label](url)` markdown links and bare emails inside
 * a plain-text line into REAL React <a> anchors. Used by BOTH the email read
 * pane (EmailThread) AND the WhatsApp message bubbles (bubbles.tsx) so a link
 * a client sends from their phone is clickable in the portal.
 *
 * SAFETY: there is no `dangerouslySetInnerHTML` on this path — every anchor is
 * a real React element, so there is no HTML-injection surface. Only
 * http(s)/mailto hrefs are ever linked; anything else renders as plain text.
 * (Extracted from the original inline copy in EmailThread.tsx, 2026-06-24.)
 */
import type { ReactNode } from 'react';
import { createElement } from 'react';

const URL_OR_MD =
  /\[([^\]]+)\]\(([^\s)]+)\)|((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?])|([\w.+-]+@[\w-]+\.[\w.-]+)/gi;

/**
 * PURE — coerce a matched URL into a safe, clickable href, or `''` to refuse.
 * A scheme-less domain (`hostinger.com`) is assumed https so it still links;
 * only http(s) + mailto schemes are allowed (no javascript:/data: etc.).
 */
export function safeHref(url: string): string {
  let u = url.trim();
  if (/^(www\.|[\w-]+\.[a-z]{2,}(?:[/?#]|$))/i.test(u) && !/^[a-z]+:/i.test(u)) {
    u = `https://${u}`;
  }
  return /^(https?:|mailto:)/i.test(u) ? u : '';
}

/**
 * PURE-ish — render one plain-text line into a node list with URLs / markdown
 * links / emails turned into anchors (new tab + rel="noopener noreferrer").
 * Non-link spans are returned as plain strings (no injection).
 */
export function linkifyText(line: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  URL_OR_MD.lastIndex = 0;
  let key = 0;
  while ((m = URL_OR_MD.exec(line)) !== null) {
    if (m.index > last) out.push(line.slice(last, m.index));
    const [, mdLabel, mdUrl, bareUrl, email] = m;
    if (mdUrl) {
      const href = safeHref(mdUrl);
      out.push(
        href
          ? createElement(
              'a',
              {
                key: key++,
                href,
                target: '_blank',
                rel: 'noopener noreferrer',
              },
              mdLabel,
            )
          : m[0],
      );
    } else if (bareUrl) {
      const href = safeHref(bareUrl);
      out.push(
        href
          ? createElement(
              'a',
              {
                key: key++,
                href,
                target: '_blank',
                rel: 'noopener noreferrer',
              },
              bareUrl,
            )
          : bareUrl,
      );
    } else if (email) {
      out.push(
        createElement('a', { key: key++, href: `mailto:${email}` }, email),
      );
    }
    last = m.index + m[0].length;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}
